import { Plane, Star, ShieldCheck } from "lucide-react";

/**
 * Banners de credibilidad de marca: Alianza Club Viajemos × American Airlines.
 * Se montan globalmente (header arriba, footer abajo) en todas las páginas.
 * Paleta: navy #0a1f44 / #13315c · oro #d4af37 / #e8c766 · marfil #f7f4ec.
 */

const HEADER_IMG = "https://customer-assets.emergentagent.com/job_078cecd6-4392-400b-98ae-d68ce0c16f0e/artifacts/h7a2pkgf_header_aa%20%281%29.jpg";
const FOOTER_IMG = "https://customer-assets.emergentagent.com/job_078cecd6-4392-400b-98ae-d68ce0c16f0e/artifacts/5eaahmt4_footer_aa.jpg";

const SERIF = "'Playfair Display', Georgia, serif";
const SANS = "'Montserrat', system-ui, sans-serif";

/* Marca tipográfica estilizada de American Airlines (navy + símbolo rojo/azul) */
const AAWordmark = ({ light = false, size = "sm" }) => {
  const txt = light ? "#f7f4ec" : "#0a1f44";
  const fs = size === "lg" ? "text-xl" : "text-sm";
  return (
    <span className="notranslate inline-flex items-center gap-1.5" translate="no" style={{ fontFamily: SANS }}>
      <span className={`font-bold tracking-tight ${fs}`} style={{ color: txt, letterSpacing: "-0.02em" }}>
        American Airlines
      </span>
      {/* Símbolo de vuelo (doble ala azul/roja) */}
      <svg width="20" height="14" viewBox="0 0 40 28" fill="none" aria-hidden="true">
        <path d="M2 26 L24 2 C26 0 30 0 31 3 L20 26 Z" fill="#0a3161" />
        <path d="M9 26 L31 2 C33 0 37 0 38 3 L27 26 Z" fill="#c8102e" />
      </svg>
    </span>
  );
};

export const AAHeaderBanner = () => (
  <div
    data-testid="aa-header-banner"
    className="relative w-full overflow-hidden border-b border-[#d4af37]/30"
    style={{ background: "linear-gradient(120deg, #0a1f44 0%, #13315c 60%, #0a1f44 100%)" }}
  >
    {/* Imagen del avión a la derecha, fundida con el navy */}
    <div
      className="absolute inset-y-0 right-0 w-[55%] sm:w-[45%] bg-cover bg-center opacity-60"
      style={{ backgroundImage: `url('${HEADER_IMG}')`, backgroundPosition: "center 35%" }}
      aria-hidden="true"
    />
    <div
      className="absolute inset-0"
      style={{ background: "linear-gradient(90deg, rgba(7,18,40,0.98) 30%, rgba(10,31,68,0.55) 65%, rgba(10,31,68,0.1) 100%)" }}
      aria-hidden="true"
    />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-4">
      <div className="min-w-0">
        {/* Subtítulo / badges */}
        <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5" style={{ fontFamily: SANS }}>
          <AAWordmark light />
          <span className="hidden sm:inline" style={{ color: "#d4af37" }}>•</span>
          <span className="hidden sm:inline text-[11px] font-medium" style={{ color: "rgba(247,244,236,0.8)" }}>Socio de vuelo certificado</span>
          <span style={{ color: "#d4af37" }}>•</span>
          <span className="text-[11px] font-medium" style={{ color: "rgba(247,244,236,0.8)" }}>20+ países</span>
        </div>
      </div>

      {/* Sello / rating a la derecha */}
      <div className="flex-shrink-0 flex flex-col items-center gap-1 pl-3 sm:pl-5 border-l border-[#d4af37]/30">
        <div className="flex items-center gap-0.5" style={{ color: "#d4af37" }}>
          {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-3 w-3 sm:h-3.5 sm:w-3.5 fill-current" />)}
        </div>
        <div className="flex items-center gap-1">
          <ShieldCheck className="h-3 w-3" style={{ color: "#d4af37" }} />
          <span className="text-[8px] sm:text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: "rgba(247,244,236,0.85)", fontFamily: SANS }}>Oficial</span>
        </div>
      </div>
    </div>
  </div>
);

export const AAFooterBanner = () => (
  <div
    data-testid="aa-footer-banner"
    className="relative w-full overflow-hidden border-t border-[#d4af37]/30 bg-[#0a1f44]"
  >
    {/* Solo la foto del avión de American Airlines */}
    <div
      className="w-full h-44 sm:h-64 lg:h-80 bg-cover bg-center"
      style={{ backgroundImage: `url('${FOOTER_IMG}')`, backgroundPosition: "center 35%" }}
      aria-label="Club Viajemos · American Airlines"
      role="img"
    />
  </div>
);
